import type { Opt } from '@jet/environment/types/optional';
import type {
    ActionSchema,
    AlbumSchema,
    ArtistSchema,
    CreativeWorkSchema,
    OpenGraphSong,
    SeoData,
    TrackRecordSEO,
    TrackSchema,
    WorkHeaderSEO,
    WorkSchema,
} from './types';
import {
    EFFECT_ID_REGEX,
    EMBEDDED_CROP_CODE_REGEX,
    FILE_TYPE_REGEX,
    QUALITY_PARAM_REGEX,
} from './constants';
import type { TrackLockupCollection } from '@jet-app/music-classical/models/section/track-lockup/TrackLockupCollection';
import { formatISODuration } from './duration';
import type { SubtitleLink } from '@jet-app/music-classical/models/header';
import type { Lockup } from '@jet-app/music-classical/models/section/Lockup';

interface SeoModifierData {
    items?: string[];
    itemNumber?: number;
}

/**
 * Determines correct pluralization key for SEO meta tags
 * Localization here will throw an error if you do not give
 * it an exact string that matches the loc string object.
 *
 * @function seoLocKeyModifier
 * @param {Array} arr - The array of values used to determmine modifier
 * @returns {String}
 */
export function seoLocKeyModifier(args: SeoModifierData): string {
    const { items, itemNumber } = args;
    const itemsLength = items?.length ?? itemNumber;

    if (!itemsLength || itemsLength === 0) {
        return '.none';
    }

    switch (itemsLength) {
        case 1: {
            return '.one';
        }

        case 2: {
            // Cases where we pass in a number won't use .two
            if (!!items) {
                return '.two';
            }

            return '.many';
        }

        default: {
            return '.many';
        }
    }
}

export const enum ACTION_SCHEMA_TYPE {
    Listen = 'ListenAction',
    View = 'ViewAction',
    Watch = 'WatchAction',
}

/**
 * @method createActionSchema
 * @param {String} url the resource canonical url
 * @param {String} type of action to create
 * @returns {ListenActionSchema|undefined} ActionSchema schema object
 */
export function createActionSchema(
    url: Opt<string>,
    actionType: ACTION_SCHEMA_TYPE,
): ActionSchema | undefined {
    if (!url) {
        return;
    }

    return {
        '@type': actionType,
        expectsAcceptanceOf: {
            '@type': 'Offer',
            category: 'free',
            price: 0,
        },
        target: {
            '@type': 'EntryPoint',
            actionPlatform: url,
        },
    };
}

/**
 * Generates the structured metadata object for a recordingOf reference
 *
 * @method schemaDataForRecordingOf
 * @param {string | WorkHeaderSEO} workHeader can be a string or a header object
 * @returns {WorkSchema}
 */
function schemaDataForRecordingOf(
    workHeader: WorkHeaderSEO | string,
): Opt<WorkSchema> {
    if (workHeader) {
        if (typeof workHeader === 'string') {
            return {
                '@type': 'MusicComposition',
                name: workHeader,
            };
        }

        const { name, workUrl, composerName, composerUrl } = workHeader;

        return {
            '@type': 'MusicComposition',
            name,
            url: workUrl,
            composer: {
                '@type': 'Person',
                name: composerName,
                url: composerUrl,
            },
            potentialAction: createActionSchema(
                workUrl,
                ACTION_SCHEMA_TYPE.Listen,
            ),
        };
    }
}

/**
 * Generates the structured metadata object for an album reference
 *
 * @method schemaDataForAlbum
 * @param {Lockup} album
 * @param {SeoData} baseSeoData
 * @returns {AlbumSchema}
 */
export function schemaDataForAlbum(
    album: Lockup,
    baseSeoData: SeoData,
): Opt<AlbumSchema> {
    const name: Opt<string> = album.title;
    const artworkUrl: Opt<string> = album.artwork?.template;
    const albumUrl = buildLinkUrl(album.url);

    if (name) {
        const schema = {
            '@type': 'MusicAlbum',
            name,
            potentialAction: createActionSchema(
                albumUrl,
                ACTION_SCHEMA_TYPE.Listen,
            ),
            url: albumUrl,
        } as AlbumSchema;

        if (artworkUrl) {
            schema.image = buildSrc(artworkUrl, {
                width: 486,
                height: 486,
                crop: 'bb',
                fileType: baseSeoData.fileType,
            });
        }

        if (album.subtitleLinks?.length) {
            schema.byArtist = album.subtitleLinks.map((subtitle) =>
                schemaDataForArtist(subtitle),
            );
        }

        return schema;
    }
}

/**
 * Generates the structured metadata object for an artist reference
 *
 * @method schemaDataForArtist
 * @param {string | SubtitleLink} artist can be a string or a Link object
 * @returns {ArtistSchema}
 */
export function schemaDataForArtist(
    artist: string | SubtitleLink,
): Opt<ArtistSchema> {
    if (artist) {
        if (typeof artist === 'string') {
            return {
                '@type': 'MusicGroup',
                name: artist,
            };
        }
        return {
            '@type': 'MusicGroup',
            name: artist.title,
            url: !!artist.href ? buildLinkUrl(artist.href) : undefined,
        };
    }
}

/**
 * Generates the structured metadata object for a subjectOf reference
 *
 * @method schemaDataForSubjectOf
 * @param {Lockup} work
 * @returns {CreativeWorkSchema}
 */
export function schemaDataForSubjectOf(work: Lockup): Opt<CreativeWorkSchema> {
    if (work) {
        if (typeof work === 'string') {
            return {
                '@type': 'CreativeWork',
                name: work,
            };
        }
        const url = buildLinkUrl(work.url);

        return {
            '@type': 'CreativeWork',
            about: {
                '@type': 'MusicGroup',
                name: work.action?.header?.composerName,
            },
            name: work.action?.header?.title,
            url,
            potentialAction: createActionSchema(url, ACTION_SCHEMA_TYPE.View),
        };
    }
}

/**
 * Generates the structured metadata object for a track reference
 *
 * @method schemaDataForTrack
 * @param {TrackRecordSEO} track
 * @returns {TrackSchema}
 */
export function schemaDataForTrack(track: TrackRecordSEO): TrackSchema {
    const schema: TrackSchema = {
        '@type': 'MusicRecording',
        name: track.title,
        duration: formatISODuration(track.duration / 1000),
        // url // TODO: Add once we support direct track urls
        recordingOf: schemaDataForRecordingOf(track.workHeader),
        offers: {
            '@type': 'Offer',
            category: 'free',
            price: 0,
        },
    };

    if (track.subtitleLinks) {
        schema.byArtist = track.subtitleLinks.map((link) =>
            schemaDataForArtist(link),
        );
    } else if (track.subtitle) {
        schema.byArtist = [schemaDataForArtist(track.subtitle)];
    }

    return schema;
}

export function getTrackOpenGraphData(track: TrackRecordSEO): OpenGraphSong {
    return {
        duration: formatISODuration(track.duration / 1000),
    };
}

interface ImageURLParams {
    width: number;
    height: number;
    crop?: string;
    fileType?: string;
    quality?: number;
}

interface ImageSettings {
    forceCropCode?: boolean; // whether to force crop code if its embedded
}

// Used only for formatting schema.org image urls
// and assumes url does not contain effect codes.
// Open Graph and Twitter images should be formatted
// by the client as there is a lot more logic involved.
export function buildSrc(
    url: string,
    urlParams: ImageURLParams,
    options?: ImageSettings,
): string {
    let updatedUrl = url;

    const { width, height, crop, fileType, quality } = urlParams;

    updatedUrl = updatedUrl
        .replace('{w}', width.toString())
        .replace('{h}', height.toString());

    if (crop) {
        if (options?.forceCropCode !== false) {
            updatedUrl = fixEmbeddedCropCode(updatedUrl, crop);
        } else {
            updatedUrl = updatedUrl.replace('{c}', crop);
        }
    }

    if (quality) {
        [updatedUrl] = replaceQualityParam(updatedUrl, quality);
    }

    if (fileType) {
        updatedUrl = updatedUrl.replace('{f}', fileType);
    }

    return updatedUrl;
}

/**
 * Removes embedded crop codes if the rawURL has an embedded crop code that is not an Effect ID
 *
 * @public
 * @param {String} rawURL
 * @param {String} crop
 * @returns {String} updated url with passed-in crop code replacing embedded crop code or crop placeholder
 */
export function fixEmbeddedCropCode(rawURL: string, crop?: string): string {
    // Normalize URL in case crop or format are hardcoded
    // Test against only the filename portion
    const stringParts = rawURL.split('/');
    const fileName = stringParts.pop();
    let url = rawURL;

    // EffectIds (e.g. SH.FPESS01) are the new artwork crop codes
    // that should not be replaced in the artwork url
    if (!fileName || EFFECT_ID_REGEX.test(fileName)) {
        return url;
    }

    const cropMatches = fileName.match(EMBEDDED_CROP_CODE_REGEX);

    // The last match will be the hard-coded crop code or the replacement indicator: {c}
    const cropMatch = cropMatches ? cropMatches.pop() : null;

    if (crop) {
        let updatedFilename;

        if (cropMatch) {
            // Update the url to include the provided crop code instead of the hard-coded crop value
            // Also update the URL to include the replacement indicator {f} if the file type is hard-coded
            updatedFilename = fileName
                .replace(EMBEDDED_CROP_CODE_REGEX, `$1x$2${crop}`)
                .replace(FILE_TYPE_REGEX, '{f}');
        } else {
            updatedFilename = fileName.replace('{c}', crop);
        }
        url = `${stringParts.join('/')}/${updatedFilename}`;
    }

    return url;
}

export function replaceQualityParam(url: string, quality?: number): string {
    const hasQualityPlaceholder = /-\{q\}/.test(url);

    // Convert url string to URL object
    // Some image URLs, like those for radio stations that are formatted with effect codes,
    // may have query params in the path which are used to build out the image with other
    // images/effects.  Ensure we only modify the image path and not the query params.
    const urlObj = new URL(url);

    if (!urlObj?.pathname) {
        return url;
    }

    // Split URL.pathname into parts, so we are only modifying the very last portion of the path
    const lastURLPartIdx = urlObj.pathname.lastIndexOf('/');
    const firstURLpart = urlObj.pathname.substring(0, lastURLPartIdx);
    let lastURLpart = decodeURI(urlObj.pathname.substring(lastURLPartIdx));

    if (quality && hasQualityPlaceholder) {
        lastURLpart = lastURLpart.replace('-{q}', `-${quality}`);
    } else if (quality && !hasQualityPlaceholder) {
        if (QUALITY_PARAM_REGEX.test(lastURLpart)) {
            // Find an optional hardcoded quality value (e.g. `-80`)
            // And then find the `.` and fileType placeholder (ext)
            lastURLpart = lastURLpart.replace(
                QUALITY_PARAM_REGEX,
                (_match, defaultQualityVal: string, fileType: string) => {
                    // only update defaultQuality if it exists in the URL
                    const defaultQuality = defaultQualityVal
                        ? defaultQualityVal.replace('-', '')
                        : quality;

                    return `-${defaultQuality}.${fileType}`;
                },
            );
        } else {
            // Effect Code URLS will have multiple instances of `.`
            // Find the last one and interpolate the quality param
            const lastDotIdx = lastURLpart.lastIndexOf('.');
            lastURLpart = `${lastURLpart.substring(
                0,
                lastDotIdx,
            )}-${quality}${lastURLpart.substring(lastDotIdx)}`;
        }
    } else if (!quality && hasQualityPlaceholder) {
        // Strip quality param if not passed a quality value
        lastURLpart = lastURLpart.replace('-{q}', '');
    }

    // Update urlObj with our modified pathname parts and then combine all
    // parts into a final string.
    urlObj.pathname = `${firstURLpart}${lastURLpart}`;
    let updatedURL = urlObj.toString();

    // Need to decode the URL string conversion to preserve curley braces in URL string.
    // Only decoding the last part of the URL, in the event that there may be intentionally
    // escaped characters in other parts of the URL.
    //
    // With decode: .../mza_4812113047298400850.png/{w}x{h}AM.RSMA01.jpg
    // Without decode: .../mza_4812113047298400850.png/%7Bw%7Dx%7Bh%7DAM.RSMA01.jpg
    updatedURL = `${updatedURL.substring(0, lastURLPartIdx)}${decodeURI(
        updatedURL.substring(lastURLPartIdx),
    )}`;

    return updatedURL;
}

/**
 * Utility to prepend the Classical origin to url paths in order
 * to create url properties for SEO and structured metadata
 *
 * @method buildLinkUrl
 * @param {String} path path of resource
 * @returns {String} path with origin prepended
 */
export function buildLinkUrl(path: string): string {
    const DEFAULT_ORIGIN = 'https://classical.music.apple.com';

    if (path.startsWith(DEFAULT_ORIGIN)) {
        return path;
    }

    return `${DEFAULT_ORIGIN}${path}`;
}

function getSeoDataFromWorkHeader(item) {
    const { firstRowLinks, secondRowLinks } = item;
    return {
        name: secondRowLinks?.[0]?.title || item.secondRowText,
        composerName: firstRowLinks?.[0]?.title || item.firstRowText,
        composerUrl:
            firstRowLinks?.[0]?.segue?.destination.contentDescriptor.url,
        workUrl: secondRowLinks?.[0]?.segue?.destination.contentDescriptor.url,
    };
}

/**
 * Utility to provide a flat track array to be used for SEO purposes.  Track lockups
 * will be an object containing the work header with an items array of the tracks
 * belonging to that work.  This utility maps the tracks out and adds the work header
 * to each track as structured metadata needs to list each track with the
 * related work as a property of that track.
 */
export function getTracksForSEO(
    trackLockups: TrackLockupCollection[],
): TrackRecordSEO[] {
    return trackLockups?.reduce((acc, trackLockup) => {
        const workHeader = !!trackLockup.header
            ? getSeoDataFromWorkHeader(trackLockup.header.item)
            : undefined;
        trackLockup.items.forEach((track) => {
            acc.push({
                ...track,
                workHeader,
            });
        });
        return acc;
    }, []);
}
